/**
 * A ticking "wait N seconds" for rate-limited auth actions (sign-in lockout,
 * forgot-password resend). The server is the authority — this only mirrors
 * its Retry-After so the button can say why it's disabled and re-enable
 * itself on time instead of letting the user hammer a 429.
 */
import { useCallback, useEffect, useState } from 'react';

/** Seconds the server told us to wait, from a 429 (body `details.retryAfter`,
 *  else the Retry-After header). Undefined for any other error. */
export function retryAfterSeconds(error: any): number | undefined {
  if (error?.response?.status !== 429) return undefined;
  const raw =
    error.response.data?.error?.details?.retryAfter ?? error.response.headers?.['retry-after'];
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : 60;
}

/** "1:05" / "45s" */
export function formatWait(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function useCooldown() {
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const remaining = Math.max(0, Math.ceil((until - now) / 1000));

  useEffect(() => {
    if (until <= Date.now()) return;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= until) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [until]);

  const start = useCallback((seconds: number) => {
    const t = Date.now();
    setNow(t);
    setUntil(t + seconds * 1000);
  }, []);

  return { remaining, active: remaining > 0, start };
}
